import NumberFlow from '@number-flow/react'

/**
 * Props for the CountBadge component
 */
export type CountBadgeProps = {
	/** The count value to display */
	count: number
	/** Optional variant for different styling contexts */
	variant?: 'column' | 'inline'
	/** Optional custom class for badge styling (column variant only) */
	className?: string
}

/**
 * CountBadge Component
 *
 * A reusable badge component that displays an animated count using NumberFlow.
 * Can be styled for different contexts like column headers or inline text.
 *
 * Features:
 * - Smooth number animations when count changes
 * - Two variants: 'column' (pill badge) and 'inline' (just the number)
 * - Accessible and semantic
 *
 * @example
 * ```tsx
 * // In a column header
 * <CountBadge count={5} variant="column" />
 *
 * // Inline in text
 * <h2>Rejected (<CountBadge count={10} variant="inline" />)</h2>
 * ```
 */
export function CountBadge({ count, variant = 'column', className }: CountBadgeProps) {
	if (variant === 'inline') {
		return <NumberFlow value={count} />
	}

	// Column variant - pill badge
	return (
		<span
			className={`px-2 py-0.5 rounded-xl text-xs font-bold inline-flex items-center justify-center min-w-[24px] ${
				className ?? 'bg-gray-200 text-gray-600'
			}`}
		>
			<NumberFlow value={count} />
		</span>
	)
}
